/**
 * Quick add-to-cart drawer.
 *
 * Wraps a native `<dialog>` opened with showModal(), so the focus trap,
 * Escape-to-close, focus return to the trigger and the inert background behind
 * it are the browser's — the same choice component-cart-drawer.js made. This
 * element adds only what the platform does not give:
 *
 *   1. opening from any `[data-quick-add-url]` trigger on the page
 *   2. fetching that product's drawer markup and injecting it
 *   3. closing on a backdrop click and on the close button
 *   4. locking the page behind it — showModal() makes the background inert but
 *      does not stop it scrolling
 *   5. getting out of the way once the item is in the cart, so the cart drawer
 *      (which opens itself on the same event) is not stacked behind this one
 *
 * The add-to-cart form inside posts to /cart/add, which assets/cart.js already
 * intercepts document-wide. Nothing here touches the cart: the badge, the cart
 * drawer and the section re-render all happen because cart.js sees the submit.
 */

const SECTION_ID = 'quick-add-drawer';

if (!customElements.get('quick-add-drawer')) {
  customElements.define(
    'quick-add-drawer',
    class QuickAddDrawer extends HTMLElement {
      static SCROLL_LOCK_CLASS = 'quick-add-drawer-open';

      constructor() {
        super();
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onInnerClick = this.onInnerClick.bind(this);
        this.onCartChange = this.onCartChange.bind(this);
        this.unlock = this.unlock.bind(this);
      }

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.requestId = 0;

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('cart:change', this.onCartChange);
        this.addEventListener('click', this.onInnerClick);
        /* `cancel` fires on Escape before `close`; listening to both means the
           page cannot be left scroll-locked if either is missed. */
        this.dialog?.addEventListener('close', this.unlock);
        this.dialog?.addEventListener('cancel', this.unlock);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('cart:change', this.onCartChange);
        this.removeEventListener('click', this.onInnerClick);
        this.dialog?.removeEventListener('close', this.unlock);
        this.dialog?.removeEventListener('cancel', this.unlock);
        document.documentElement.classList.remove(QuickAddDrawer.SCROLL_LOCK_CLASS);
      }

      get content() {
        return this.querySelector('[data-quick-add-content]');
      }

      onDocumentClick(event) {
        const trigger = event.target.closest('[data-quick-add-url]');
        if (!trigger) return;

        const url = trigger.getAttribute('data-quick-add-url');
        if (!url) return;

        /* Without JavaScript the trigger is a link to the product page, which is
           the correct fallback, so the default is only prevented once we know we
           can handle it. */
        event.preventDefault();
        this.open(url);
      }

      onInnerClick(event) {
        /* With showModal(), a click on the backdrop targets the dialog itself. */
        if (event.target === this.dialog || event.target.closest('[data-quick-add-close]')) {
          this.close();
        }
      }

      onCartChange(event) {
        if (event.detail?.action === 'add') this.close();
      }

      /**
       * Opens the drawer and loads one product into it.
       * @param {string} url Product URL to render the drawer content from
       */
      async open(url) {
        if (!this.dialog) return;

        /* Lock first: showModal() scrolls the newly focused control into view,
           and with the page still scrollable that reads as a jump behind the
           drawer. */
        document.documentElement.classList.add(QuickAddDrawer.SCROLL_LOCK_CLASS);
        if (!this.dialog.open) this.dialog.showModal();
        this.setBusy(true);

        /* A shopper can click a second card while the first fetch is in flight.
           Stamping each request and dropping stale replies means the drawer
           always shows the product that was asked for last, not whichever
           response happened to land last. */
        this.requestId += 1;
        const requestId = this.requestId;

        try {
          const separator = url.includes('?') ? '&' : '?';
          const response = await fetch(`${url}${separator}section_id=${SECTION_ID}`);
          if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

          const markup = await response.text();
          if (requestId !== this.requestId || !this.content) return;

          this.content.innerHTML = markup;
          this.upgradeScripts();
          this.setBusy(false);
          this.content.querySelector('[data-quick-add-close]')?.focus();
        } catch (error) {
          if (requestId !== this.requestId) return;
          /* Closing beats showing an empty panel: the trigger is still a link to
             the product page, so the shopper is one click from the real thing. */
          console.error('Quick add drawer could not load the product', error);
          this.close();
        }
      }

      close() {
        this.dialog?.close();
        /* Also here, not only on the `close` event: Escape and other
           browser-initiated closes never call this method, and a page left
           scroll-locked is the worse failure to risk. The unlock is idempotent. */
        this.unlock();
      }

      unlock() {
        document.documentElement.classList.remove(QuickAddDrawer.SCROLL_LOCK_CLASS);
      }

      setBusy(isBusy) {
        this.dialog?.toggleAttribute('data-loading', isBusy);
      }

      /**
       * Re-creates the injected `<script>` tags.
       *
       * Scripts written with innerHTML never execute, so the carousel inside the
       * drawer would stay inert without this. The module URLs are the theme's own
       * assets and the browser de-duplicates a module it has already evaluated.
       */
      upgradeScripts() {
        this.content?.querySelectorAll('script').forEach((old) => {
          const script = document.createElement('script');
          for (const { name, value } of old.attributes) script.setAttribute(name, value);
          script.textContent = old.textContent;
          old.replaceWith(script);
        });
      }
    },
  );
}
